/**
 * InsightsPanel Component
 * Displays AI-powered recommendations, anomaly alerts, and chat interface
 * Requirements: 5.1, 5.5, 5.6
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fetchInsights,
  chatWithAI,
  Recommendation,
  Anomaly,
  ChatMessage,
  ChatResponse,
} from '../services/api';

interface InsightsPanelProps {
  siteId: string;
  siteName: string;
}

export function InsightsPanel({ siteId, siteName }: InsightsPanelProps) {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'recommendations' | 'anomalies' | 'chat'>('recommendations');
  
  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [suggestedFollowUps, setSuggestedFollowUps] = useState<string[]>([]);

  useEffect(() => {
    async function loadInsights() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchInsights(siteId);
        setRecommendations(data.recommendations);
        setAnomalies(data.anomalies);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load insights');
      } finally {
        setLoading(false);
      }
    }
    loadInsights();
  }, [siteId]);

  const handleSendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    const userMessage: ChatMessage = { role: 'user', content: message };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response: ChatResponse = await chatWithAI({
        siteId,
        message,
        context: {
          previousMessages: chatMessages,
        },
      });

      const assistantMessage: ChatMessage = { role: 'assistant', content: response.response };
      setChatMessages(prev => [...prev, assistantMessage]);
      setSuggestedFollowUps(response.suggestedFollowUps);
    } catch (err) {
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  }, [siteId, chatMessages]);

  const getPriorityColor = (priority: Recommendation['priority']) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
    }
  };

  const getSeverityColor = (severity: Anomaly['severity']) => {
    return severity === 'warning' ? '#f59e0b' : '#3b82f6';
  };

  const getTypeIcon = (type: Recommendation['type']) => {
    switch (type) {
      case 'capacity_fill': return '📦';
      case 'cost_optimization': return '💰';
      case 'format_priority': return '📊';
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>AI Insights</h3>
        </div>
        <div style={styles.loading}>Loading insights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>AI Insights</h3>
        </div>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container} data-testid="insights-panel">
      <div style={styles.header}>
        <h3 style={styles.title}>AI Insights for {siteName}</h3>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'recommendations' ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab('recommendations')}
        >
          Recommendations ({recommendations.length})
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'anomalies' ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab('anomalies')}
        >
          Alerts ({anomalies.length})
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'chat' ? styles.activeTab : {}),
          }}
          onClick={() => setActiveTab('chat')}
        >
          Ask AI
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.content}>
        {activeTab === 'recommendations' && (
          <RecommendationsTab
            recommendations={recommendations}
            getPriorityColor={getPriorityColor}
            getTypeIcon={getTypeIcon}
          />
        )}
        {activeTab === 'anomalies' && (
          <AnomaliesTab anomalies={anomalies} getSeverityColor={getSeverityColor} />
        )}
        {activeTab === 'chat' && (
          <ChatTab
            messages={chatMessages}
            input={chatInput}
            loading={chatLoading}
            suggestedFollowUps={suggestedFollowUps}
            onInputChange={setChatInput}
            onSendMessage={handleSendMessage}
          />
        )}
      </div>
    </div>
  );
}


// Recommendations Tab Component
interface RecommendationsTabProps {
  recommendations: Recommendation[];
  getPriorityColor: (priority: Recommendation['priority']) => string;
  getTypeIcon: (type: Recommendation['type']) => string;
}

function RecommendationsTab({ recommendations, getPriorityColor, getTypeIcon }: RecommendationsTabProps) {
  if (recommendations.length === 0) {
    return <div style={styles.empty}>No recommendations available.</div>;
  }

  return (
    <div style={styles.list}>
      {recommendations.map(rec => (
        <div key={rec.id} style={styles.card} data-testid="recommendation-card">
          <div style={styles.cardHeader}>
            <span style={styles.typeIcon}>{getTypeIcon(rec.type)}</span>
            <span style={styles.cardTitle}>{rec.title}</span>
            <span
              style={{
                ...styles.priorityBadge,
                backgroundColor: getPriorityColor(rec.priority),
              }}
            >
              {rec.priority}
            </span>
          </div>
          <p style={styles.description}>{rec.description}</p>
          <div style={styles.reasoning} data-testid="recommendation-reasoning">
            <strong>Reasoning:</strong> {rec.reasoning}
          </div>
          <div style={styles.impact}>
            <span style={styles.impactLabel}>Impact:</span>
            <span style={styles.impactValue}>
              {rec.impact.metric}: {rec.impact.currentValue.toFixed(2)} → {rec.impact.projectedValue.toFixed(2)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Anomalies Tab Component
interface AnomaliesTabProps {
  anomalies: Anomaly[];
  getSeverityColor: (severity: Anomaly['severity']) => string;
}

function AnomaliesTab({ anomalies, getSeverityColor }: AnomaliesTabProps) {
  if (anomalies.length === 0) {
    return <div style={styles.empty}>No anomalies detected. Everything looks good!</div>;
  }

  return (
    <div style={styles.list}>
      {anomalies.map(anomaly => (
        <div
          key={anomaly.id}
          style={{
            ...styles.card,
            borderLeft: `4px solid ${getSeverityColor(anomaly.severity)}`,
          }}
          data-testid="anomaly-alert"
        >
          <div style={styles.cardHeader}>
            <span style={styles.alertIcon}>{anomaly.severity === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span style={styles.cardTitle}>{anomaly.format}</span>
            <span
              style={{
                ...styles.severityBadge,
                backgroundColor: getSeverityColor(anomaly.severity),
              }}
            >
              {anomaly.type.replace('_', ' ')}
            </span>
          </div>
          <p style={styles.description}>{anomaly.description}</p>
          <div style={styles.suggestedAction}>
            <strong>Suggested Action:</strong> {anomaly.suggestedAction}
          </div>
        </div>
      ))}
    </div>
  );
}

// Chat Tab Component
interface ChatTabProps {
  messages: ChatMessage[];
  input: string;
  loading: boolean;
  suggestedFollowUps: string[];
  onInputChange: (value: string) => void;
  onSendMessage: (message: string) => void;
}

function ChatTab({ messages, input, loading, suggestedFollowUps, onInputChange, onSendMessage }: ChatTabProps) {
  return (
    <div style={styles.chatContainer}>
      <div style={styles.chatMessages} data-testid="chat-messages">
        {messages.length === 0 && (
          <div style={styles.chatWelcome}>
            Ask me anything about this site's capacity, costs, or optimization opportunities.
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              ...styles.chatMessage,
              ...(msg.role === 'user' ? styles.userMessage : styles.assistantMessage),
            }}
          >
            {msg.content}
          </div>
        ))}
        {loading && (
          <div style={{ ...styles.chatMessage, ...styles.assistantMessage }}>
            Thinking...
          </div>
        )}
      </div>

      {suggestedFollowUps.length > 0 && (
        <div style={styles.followUps}>
          {suggestedFollowUps.map((followUp, index) => (
            <button
              key={index}
              style={styles.followUpButton}
              onClick={() => onSendMessage(followUp)}
            >
              {followUp}
            </button>
          ))}
        </div>
      )}

      <div style={styles.chatInputContainer}>
        <input
          type="text"
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSendMessage(input)}
          placeholder="Ask a question..."
          style={styles.chatInput}
          disabled={loading}
          data-testid="chat-input"
        />
        <button
          onClick={() => onSendMessage(input)}
          disabled={loading || !input.trim()}
          style={styles.sendButton}
          data-testid="chat-send"
        >
          Send
        </button>
      </div>
    </div>
  );
}


// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e5e7eb',
  },
  tab: {
    flex: 1,
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#6b7280',
    transition: 'all 0.2s',
  },
  activeTab: {
    color: '#2563eb',
    borderBottom: '2px solid #2563eb',
    fontWeight: 500,
  },
  content: {
    padding: '16px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  loading: {
    padding: '32px',
    textAlign: 'center',
    color: '#6b7280',
  },
  error: {
    padding: '32px',
    textAlign: 'center',
    color: '#dc2626',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: '#6b7280',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  typeIcon: {
    fontSize: '20px',
  },
  alertIcon: {
    fontSize: '18px',
  },
  cardTitle: {
    flex: 1,
    fontWeight: 600,
    color: '#111827',
  },
  priorityBadge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    textTransform: 'uppercase',
  },
  severityBadge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    textTransform: 'capitalize',
  },
  description: {
    margin: '0 0 12px 0',
    color: '#4b5563',
    lineHeight: 1.5,
  },
  reasoning: {
    padding: '12px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#374151',
    marginBottom: '12px',
  },
  impact: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  impactLabel: {
    fontWeight: 500,
    color: '#6b7280',
  },
  impactValue: {
    color: '#059669',
    fontWeight: 500,
  },
  suggestedAction: {
    padding: '12px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#374151',
  },
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '400px',
  },
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  chatWelcome: {
    textAlign: 'center',
    color: '#6b7280',
    padding: '32px',
  },
  chatMessage: {
    padding: '12px 16px',
    borderRadius: '12px',
    maxWidth: '80%',
    lineHeight: 1.5,
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#2563eb',
    color: '#fff',
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#f3f4f6',
    color: '#111827',
  },
  followUps: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '8px',
    borderTop: '1px solid #e5e7eb',
  },
  followUpButton: {
    padding: '6px 12px',
    backgroundColor: '#e5e7eb',
    border: 'none',
    borderRadius: '16px',
    fontSize: '13px',
    color: '#374151',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  chatInputContainer: {
    display: 'flex',
    gap: '8px',
    padding: '8px',
    borderTop: '1px solid #e5e7eb',
  },
  chatInput: {
    flex: 1,
    padding: '10px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '20px',
    fontSize: '14px',
    outline: 'none',
  },
  sendButton: {
    padding: '10px 20px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default InsightsPanel;
